export default {
  selectProperty: (stateKey, valueKey, chosenValue, setState, defaultValue, callback = null) => {
    const value = chosenValue ? chosenValue[valueKey] : null;
    if (!value && defaultValue === undefined) { // eslint-disable-line no-undefined
      return;
    }

    setState(({ formData }) => ({
      formData: {
        ...formData,
        [stateKey]: {
          ...formData[stateKey],
          value,
        },

      },
      isFormDirty: true,
    }), () => {
      if (callback) {
        callback();
      }
    });
  },

  changeProperty: ({ event, setState, callback }) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;

    const propertyName = event.target.name;

    setState(({ formData }) => ({
      formData: {
        ...formData,
        [propertyName]: {
          ...formData[propertyName],
          value,
        },
      },
      isFormDirty: true,
    }), () => {
      if (callback) {
        callback();
      }
    });
  },

  changeCheckboxGroupProperty: ({ stateKey, groupValues, setState, callback }) => {
    setState(({ formData }) => ({
      formData: {
        ...formData,
        [stateKey]: {
          ...formData[stateKey],
          value: groupValues,
        },
      },
      isFormDirty: true,
    }), () => {
      if (callback) {
        callback();
      }
    });
  },

  changeRadioProperty: (stateKey, value, setState, callback) => {
    setState(({ formData }) => ({
      formData: {
        ...formData,
        [stateKey]: {
          ...formData[stateKey],
          value,
        },
      },
      isFormDirty: true,
    }), () => {
      if (callback) {
        callback();
      }
    });
  },

  resetValue: ({ key, initialFormDataState, setState, callback }) => {
    setState(({ formData }) => ({
      formData: {
        ...formData,
        [key]: {
          ...formData[key],
          value: initialFormDataState[key].value,
          isValid: initialFormDataState[key].isValid,
        },
      },
      isFormDirty: true,
    }), () => {
      if (callback) {
        callback();
      }
    });
  },

  validate: ({ key, isValid, setState, callback }) => {
    setState(({ formData }) => ({
      formData: {
        ...formData,
        [key]: {
          ...formData[key],
          isValid,
        },
      },
    }), () => {
      if (callback) {
        callback();
      }
    });
  },

  validateAll: (setState, { filesRequired = true } = {}) => {
    setState(({ elementsToValidate, globalElementsToValidate, formData, ...rest }) => {
      const isGlobalFormValid = !globalElementsToValidate
        ? true
        : globalElementsToValidate.reduce(
          (isValid, current) => isValid && rest[current],
          true
        );
      const isFormValid = elementsToValidate.reduce(
        (isValid, current) => isValid && formData[current].isValid,
        true
      );

      return {
        isFormValid: Boolean(isGlobalFormValid
          && isFormValid
          && (filesRequired && formData.files ? formData.files.length : true)),
      };
    });
  },

  validationSubscription: ({ key, operation, setState, callback }) => {
    if (operation === 'subscribe') {
      setState(({ elementsToValidate }) => ({
        elementsToValidate: [
          ...elementsToValidate,
          key,
        ],
      }), () => {
        callback();
      });
    }

    if (operation === 'unsubscribe') {
      setState(({ elementsToValidate }) => ({
        elementsToValidate: elementsToValidate.filter(
          element => element !== key
        ),
      }), () => {
        callback();
      });
    }
  },

  resetForm: ({ initialState, setState, callback }) => setState({
    formData: initialState,
    isFormValid: false,
    isFormDirty: false,
    triedToSubmitForm: false,
  }, () => {
    if (callback) {
      callback();
    }
  }),

  addFileToForm: ({ file, setState, callback }) => {
    setState(({ formData }) => ({
      formData: {
        ...formData,
        files: [
          ...formData.files,
          file,
        ],
      },
      isFormDirty: true,
    }), () => {
      callback();
    });
  },

  addSingleFileToForm: ({ file, setState, callback }) => {
    setState(({ formData }) => ({
      formData: {
        ...formData,
        files: file ? [file] : [],
      },
      isFormDirty: true,
    }), () => {
      if (callback) {
        callback();
      }
    });
  },

  removeFileFromForm: ({ fileToRemove, setState, callback }) => {
    setState(({ formData }) => ({
      formData: {
        ...formData,
        files: formData.files.filter(
          ({ fileName }) => fileName !== fileToRemove.fileName
        ),
      },
      isFormDirty: true,
    }), () => {
      callback();
    });
  },

  validateUniqueFields: ({
    formData,
    list,
    isUnique = true,
    key,
    comparator,
    setState,
    callback,
  }) => {
    const foundElement = list.find(element => comparator(element, formData));

    const callCallback = () => callback && callback(foundElement);

    if (foundElement) {
      setState(state => ({
        ...state,
        [key]: false,
      }), callCallback);
    } else if (!isUnique) {
      setState(state => ({
        ...state,
        [key]: true,
      }), callCallback);
    }
  },

  selectMultiValueProperty: ({
    stateKey,
    value,
    valueCompareKey,
    isSelected,
    allowDuplicates = true,
    valueSelector = (element, incomingValue) => element[valueCompareKey] === incomingValue[valueCompareKey],
    setState,
    callback,
  }) => {
    setState(({ formData }) => {
      if (isSelected && allowDuplicates) {
        return {
          formData: {
            ...formData,
            [stateKey]: {
              ...formData[stateKey],
              value: formData[stateKey].value.concat(value),
            },
          },
          isFormDirty: true,
        };
      }

      if (isSelected && !allowDuplicates) {
        const duplicateIndex = formData[stateKey].value.findIndex(
          element => valueSelector(element, value)
        );

        const elementToUpdate = {
          ...formData[stateKey].value[duplicateIndex],
          ...value,
        };

        return {
          formData: {
            ...formData,
            [stateKey]: {
              ...formData[stateKey],
              value: [
                ...formData[stateKey].value.slice(0, duplicateIndex),
                elementToUpdate,
                ...formData[stateKey].value.slice(duplicateIndex + 1),
              ],
            },
          },
          isFormDirty: true,
        };
      }

      return {
        formData: {
          ...formData,
          [stateKey]: {
            ...formData[stateKey],
            value: formData[stateKey].value.filter(element => !valueSelector(element, value)),
          },
        },
        isFormDirty: true,
      };
    }, () => {
      if (callback) {
        callback();
      }
    });
  },
};
